import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { useTheme } from 'styled-components'

import {
  Button,
  ButtonWithSpinner,
  Card,
  EmailInput,
  InputWithLabel,
  RestorePasswordModal,
  Text,
  useFetch,
} from '@reveni-io/reveni-ui'

import AppContext from '@/common/context/app.context'
import { flattenReturnPortalsFromStores, getSelectedReturnPortal, getSelectedStore } from '@/common/helpers/helpers'
import commonServices from '@/common/services/services'
import generalServices from '@/general/services/services'
import routes from '@/Router/routes.constants'

import useImpersonationLogin from './hooks/useImpersonationLogin'
import * as Styles from './styles'

const LoginPage = () => {
  const theme = useTheme()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showRestorePasswordModal, setShowRestorePasswordModal] = useState(false)
  const { fetchData: getToken, loading } = useFetch(generalServices.getToken, { doInitialRequest: false })
  const { fetchData: restorePassword, loading: restoring } = useFetch(generalServices.restorePassword, {
    doInitialRequest: false,
  })
  const { fetchData: getShopInfo, loading: gettingShopInfo } = useFetch(commonServices.getShopInfo, {
    doInitialRequest: false,
  })
  const { fetchData: getUserProfile, loading: gettingUser } = useFetch(commonServices.getUserProfile, {
    doInitialRequest: false,
  })
  const { updateContext } = useContext(AppContext)
  const navigation = useNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const [error, setError] = useState('')
  const location = useLocation()

  const { error: impersonationError } = useImpersonationLogin({
    getShopInfo,
    getUserProfile,
    updateContext,
    location,
  })

  const onSubmit = async e => {
    e.preventDefault()
    try {
      const data = await getToken({ email, password })
      localStorage.jwt = data.access
      localStorage.refresh = data.refresh
      localStorage.removeItem('isImpersonating')

      const shopInfo = await getShopInfo()

      const userInfo = await getUserProfile()
      const returnPortals = flattenReturnPortalsFromStores(shopInfo.stores)
      const selectedStore = getSelectedStore(shopInfo.stores, userInfo?.default_store_id)

      updateContext(info => ({
        ...info,
        ...shopInfo,
        userInfo,
        selectedStore,
        returnPortals,
        selectedReturnPortal: getSelectedReturnPortal(returnPortals, selectedStore?.default_portal_id),
      }))

      const next = searchParams.get('next')
      navigation(next || routes.dashboardPage, { state: { from: location } })
    } catch (err) {
      setError(err.errorMessage)
    }
  }

  const handleChange = (e, change) => {
    change(e.target.value)
  }

  const formIsValid = () => email && password

  const onRestorePassword = email => {
    restorePassword({ email })
    setShowRestorePasswordModal(false)
  }

  return (
    <Styles.Layout>
      <Card>
        <Styles.MainContainer>
          <Styles.ImageContainer>
            <Styles.ReveniLogo src="/logos/logo_reveni.svg" alt="Reveni logo" />
          </Styles.ImageContainer>
          <Styles.HeaderTexts>
            <Text customStyles={theme.texts.allCapsL} color={theme.colors.text.subtle}>
              {t('empresas')}
            </Text>
            <Text tag="h1" customStyles={theme.texts.titleXXL} color={theme.colors.text.default}>
              {t('Iniciar sesión')}
            </Text>
          </Styles.HeaderTexts>
          <form onSubmit={onSubmit}>
            <EmailInput
              error={error || impersonationError}
              onChange={e => handleChange(e, setEmail)}
              placeholder="emmett@brown.com"
            />
            <InputWithLabel
              error={error || impersonationError}
              placeholder="******"
              onChange={e => handleChange(e, setPassword)}
              type="password"
              label={t('Contraseña')}
              fullWidth
            />
            <Styles.ActionsContainer>
              {(error || impersonationError) && <Text type="bodySRegular">{error || impersonationError}</Text>}
              <ButtonWithSpinner
                disabled={!formIsValid() || loading || gettingShopInfo || gettingUser}
                loading={loading || gettingShopInfo || gettingUser}
                defaultLabel={t('Iniciar sesión')}
                loadingLabel={t('Iniciando')}
                fullWidth
              />
            </Styles.ActionsContainer>
          </form>
          <Button variant="text" onClick={() => setShowRestorePasswordModal(true)}>
            {t('He olvidado mi contraseña')}
          </Button>
          <RestorePasswordModal
            show={showRestorePasswordModal}
            restoring={restoring}
            onClose={() => setShowRestorePasswordModal(false)}
            onRestore={onRestorePassword}
          />
        </Styles.MainContainer>
      </Card>
    </Styles.Layout>
  )
}

export default LoginPage
